import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Spin, Empty } from "antd";
import { Link, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { WHITE, GRAY_500, GRAY_400, PRIMARY_600, BLACK } from "../../constant/colors";
import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import { selectSettings } from "../../store/selector";
import { parseValues } from "../../hooks/getSetting";
import { usePortalCredentials } from "../../hooks/usePortalCredentials";
import { usePortalCredentialsEnabled } from "../../hooks/usePortalCredentialsEnabled";
import { PortalCredentialCard } from "./PortalCredentialCard";
import { PortalCredentialModal } from "./PortalCredentialModal";

const { Text } = Typography;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
`;

const BreadcrumbSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const BreadcrumbLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: ${PRIMARY_600};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${GRAY_500};
  font-size: 18px;
`;

const BreadcrumbCurrent = styled.span`
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: ${BLACK};
`;

const PageDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  display: block;
  margin-bottom: 24px;
`;

const ContentSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(346px, 1fr));
  gap: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
`;

const DisclaimerSection = styled.div`
  flex-shrink: 0;
  padding: 16px;
  margin-top: 16px;
  border-top: 1px solid ${GRAY_400};
`;

const DisclaimerText = styled(Text)`
  font-size: 12px;
  color: ${GRAY_500};
  font-style: italic;
`;

const PortalCredentialsPage = () => {
  const { t } = useTranslation();
  const allSettings = useSelector(selectSettings);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [selectedPortalId, setSelectedPortalId] = useState(null);

  const { credentials, isLoading, isSaving, deletingId, saveCredential, removeCredential, getCredentialForPortal } =
    usePortalCredentials();

  const supportedPortals = useMemo(() => {
    const setting = allSettings["portal-credentials.supported-portals"];
    if (!setting) return [];
    try {
      const parsed = parseValues(setting);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [allSettings]);

  // Check if user is authorized to access portal credentials
  const isPortalCredentialsEnabled = usePortalCredentialsEnabled();

  const disclaimerText = useMemo(() => {
    const setting = allSettings["portal-credentials.disclaimer-text"];
    if (!setting) return t("portal_credentials.disclaimer_default");
    const parsed = parseValues(setting);
    return parsed || t("portal_credentials.disclaimer_default");
  }, [allSettings, t]);

  // Redirect unauthorized users to settings page
  if (!isPortalCredentialsEnabled) {
    return <Redirect to="/settings" />;
  }

  const handleAddCredential = (portalId) => {
    setSelectedPortalId(portalId);
    setEditingCredential(null);
    setModalVisible(true);
  };

  const handleEditCredential = (credential) => {
    setSelectedPortalId(credential.portalId);
    setEditingCredential(credential);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCredential(null);
    setSelectedPortalId(null);
  };

  const handleSaveCredential = async (portalId, username, password) => {
    const result = await saveCredential(portalId, username, password);
    if (result.success) {
      handleModalClose();
    }
    return result;
  };

  const handleDeleteCredential = async (credentialId) => {
    return await removeCredential(credentialId);
  };

  const getPortalInfo = (portalId) => {
    return supportedPortals.find((p) => p.id === portalId);
  };

  if (isLoading && credentials.length === 0) {
    return (
      <PageContainer>
        <HeaderSection>
          <BreadcrumbSection>
            <BreadcrumbLink to="/settings">
              <SettingsIcon />
              {t("navigation.settings")}
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbCurrent>{t("portal_credentials.page_title")}</BreadcrumbCurrent>
          </BreadcrumbSection>
        </HeaderSection>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderSection>
        <BreadcrumbSection>
          <BreadcrumbLink to="/settings">
            <SettingsIcon />
            {t("navigation.settings")}
          </BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbCurrent>{t("portal_credentials.page_title")}</BreadcrumbCurrent>
        </BreadcrumbSection>
        <PageDescription>{t("portal_credentials.page_subtitle")}</PageDescription>
      </HeaderSection>

      <ContentSection>
        {supportedPortals.length === 0 ? (
          <EmptyContainer>
            <Empty description={t("portal_credentials.no_credentials")} />
          </EmptyContainer>
        ) : (
          <CardsGrid>
            {supportedPortals.map((portal) => {
              const credential = getCredentialForPortal(portal.id);
              return (
                <PortalCredentialCard
                  key={portal.id}
                  portal={portal}
                  credential={credential}
                  onAdd={() => handleAddCredential(portal.id)}
                  onEdit={() => handleEditCredential(credential)}
                  onDelete={() => handleDeleteCredential(credential?.id)}
                  isDeleting={deletingId === credential?.id}
                />
              );
            })}
          </CardsGrid>
        )}
      </ContentSection>

      {disclaimerText && (
        <DisclaimerSection>
          <DisclaimerText>{disclaimerText}</DisclaimerText>
        </DisclaimerSection>
      )}

      <PortalCredentialModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSave={handleSaveCredential}
        portal={getPortalInfo(selectedPortalId)}
        credential={editingCredential}
        isSaving={isSaving}
        supportedPortals={supportedPortals}
      />
    </PageContainer>
  );
};

export { PortalCredentialsPage };
